// src/pages/POS/PosEntry.jsx
import React from "react";
import { Row, Col, Grid, Button, Flex } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import CreatePosForm from "./components/CreatePosForm";
import PosTable from "./components/PosTable";
import { createSales, fetchPosVoucherNumber } from "./posThunk";
import { resetPos, setCustomer, setCartMode } from "./posSlice";
import { resetLookups } from "../../store/commonSlices/lookupsSlice";
import Swal from "sweetalert2";
import { selectAuthInfo } from "../Auth/authSelectors";
import { fetchPreviousDue } from "../../store/commonThunks/lookups";

const PosEntry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {useBreakpoint} = Grid;
  const screens = useBreakpoint();

  const authInfo = useSelector(selectAuthInfo);
  const { itemCart, customer, footerValues, voucherNumber, payCart, cartMode } =
    useSelector((s) => s.pos);

  const handleSave = () => {

    const hasConversionRate = Number(footerValues?.conversion_rate) > 0;
    
    const maxAllowedCartTotal = hasConversionRate
      ? Number(footerValues.grandTotal) + 0.1
      : Number(footerValues.grandTotal);

    // 1️⃣ Validate customer selection
    if (!customer || !customer.acc_id) {
      Swal.fire("Please select a customer!", "", "warning");
      return;
    }

    // 1️⃣ Validate item cart (skip blanks)
    const validItems = (itemCart || []).filter((item) => item.item_id);
    if (validItems.length === 0) {
      Swal.fire("Item cart is Empty! Add Items", "", "warning");
      return;
    }

    // 2️⃣ Validate quantities (every item must have qty > 0)
    const allValidQty = validItems.every((item) => Number(item.qty || 0) > 0);
    if (!allValidQty) {
      Swal.fire("All items must have quantity greater than 0!", "", "warning");
      return;
    }

    // 2️⃣ Validate customer
    if (!customer || !customer.acc_id) {
      Swal.fire("Select a customer!", "", "warning");
      return;
    }

    // 3️⃣ Validate totals
    if (Number(footerValues.grandTotal || 0) <= 0) {
      Swal.fire("Total Amount should not be 0!", "", "warning");
      return;
    }

    // 4️⃣ Validate paid vs payment cart
    const payCartTotal = payCart?.reduce(
      (sum, p) => sum + (parseFloat(p.tran_amount) || 0),
      0
    );
    const paid = Number(footerValues.paidAmount) || 0;
    if (Math.abs(paid - payCartTotal) > 0.01) {
      Swal.fire("Paid Amount and Payment Cart Amount must be the same!", "", "warning");
      return;
    }
    if (Number(payCartTotal.toFixed(2)) > Number(maxAllowedCartTotal.toFixed(2))) {      
       Swal.fire({
        title: "Payment amount exceeds total!",
        text: `Cart Total (${payCartTotal.toFixed(
          2
        )}) should not exceed Grand Total (${Number(footerValues.grandTotal).toFixed(2)})`,
        icon: "warning",
      });
      return;
    }

    const stockInvalidItem = validItems.find(
  (item) => Number(item.qty) > Number(item.itemInfo?.current_qty || 0)
);
if (stockInvalidItem) {
  Swal.fire(
    `Stock not available for ${stockInvalidItem.product}!`,
    `Available: ${stockInvalidItem.itemInfo?.current_qty || 0}`,
    "warning"
  );
  return;
}

    // ✅ Save
    Swal.fire({
      title: "Saving POS Entry...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    dispatch(createSales({ location }))
      .unwrap()
      .then((res) => {
        Swal.close();
        if (!res.error) {
          // reset form after success
          dispatch(setCustomer(null));
          dispatch(fetchPosVoucherNumber());

          Swal.fire({
            title: "POS Entry Created! Do you want to view voucher?",
            icon: "success",
            showCancelButton: true,
            confirmButtonText: "Yes, view voucher",
            cancelButtonText: "No, back",
          }).then((result) => {
            dispatch(resetPos());
            if (result.isConfirmed) {
              const id = res.sale_o_id || res.sale_id || res.id;
              navigate(`/sales/quick-pos/${id}`);
            } else {
              dispatch(resetPos());
              dispatch(resetLookups());
              navigate("/pos/pos-entry");
            }
          });
        } else {
          Swal.fire(res.message || "Something went wrong", "", "warning");
        }
      })
      .catch(() => {
        Swal.close();
        Swal.fire("Network ERROR", "", "error");
      });
  };

  return (
    <div style={{ padding: screens.xs ? 12 : 20, overflowX: "hidden", width: "100%" }}>
      {/* Top Buttons */}
      {screens.md && <Row>
        <Col span={24}>
          <Flex justify="flex-end" gap={16} style={{ marginBottom: 16 }}>
            <Button onClick={() => navigate("/sales-dashboard")}>Back</Button>
            <Button onClick={() => {dispatch(resetPos()); dispatch(fetchPosVoucherNumber())}}>Reset</Button>
            <Button type="primary" onClick={handleSave}>
              Save
            </Button>
          </Flex>
        </Col>
      </Row>}

      {/* Form + Table */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {/* ✅ Cart Mode Switch */}
          <Flex gap={16} style={{ marginBottom: 16 }}>
            <Button
              type={cartMode === "auto" ? "primary" : "default"}
              onClick={() => dispatch(setCartMode("auto"))}
            >
              Auto Cart
            </Button>
            <Button
              type={cartMode === "manual" ? "primary" : "default"}
              onClick={() => dispatch(setCartMode("manual"))}
              data-testid="btn-manual-cart"
            >
              Manual Cart
            </Button>
          </Flex>

          <CreatePosForm authInfo={authInfo} />
        </Col>
        <Col span={24}>
          <PosTable authInfo={authInfo} />
        </Col>
      </Row>

      {/* Bottom Buttons */}
      <Row>
        <Col span={24}>
          <Flex justify="flex-end" gap={16} style={{ marginTop: 16 }}>
            <Button onClick={() => navigate("/sales-dashboard")}>Back</Button>
            <Button onClick={() => {dispatch(resetPos()); dispatch(fetchPosVoucherNumber());}}>Reset</Button>
            <Button type="primary" onClick={handleSave} data-testid="final-save-btn">
              Save
            </Button>
          </Flex>
        </Col>
      </Row>
    </div>
  );
};

export default PosEntry;
